// Display of money from the financial layer: the backend sends BIGINT minor
// units as a string (v2-fin-arch §19.2) — NEVER compute in float. Above
// 2^53 (Number.MAX_SAFE_INTEGER) composing a `Number` from the bigint loses
// precision, so `formatCurrency`/`formatAmount` never do that: they read the
// exact integer/fraction digit strings off the bigint and splice them into
// the locale's own grouping/decimal/currency template, which is all
// `Intl.NumberFormat.formatToParts` is asked for (a fixed probe magnitude
// that reliably exercises grouping and, when the amount is negative, the
// locale's minus-sign placement).

import { getNumberSeparators } from '@mfw/components';
import escapeRegExp from 'lodash/escapeRegExp';

// `BigInt(100)`, not a `100n` literal: bigint literals need an ES2020+
// target and this project's `tsconfig.json` targets ES6.
const MINOR_UNITS_PER_MAJOR = BigInt(100);
// Any probe with a decimal part and more than 3 integer digits reveals the
// locale's group/decimal separators and full part ordering (currency
// placement, minus-sign placement). The probe's OWN digits are discarded —
// only its part *types* and separator/currency/literal *values* are reused.
const POSITIVE_PROBE = 1234.56;
const NEGATIVE_PROBE = -1234.56;

/** Re-groups a plain digit string (no sign) into the locale's thousands
 *  grouping, e.g. `groupIntegerDigits('9007199254740993', '.')` →
 *  `'9.007.199.254.740.993'`. */
function groupIntegerDigits(digits: string, groupSeparator: string): string {
  if (digits.length <= 3) return digits;
  const chunks: string[] = [];
  let i = digits.length;
  while (i > 3) {
    chunks.unshift(digits.slice(i - 3, i));
    i -= 3;
  }
  chunks.unshift(digits.slice(0, i));
  return chunks.join(groupSeparator);
}

/** Splices exact `majorDigits`/`fractionDigits` into the SHAPE of `parts`
 *  (from `Intl.NumberFormat.formatToParts` on a probe number of the same
 *  sign): every `integer`/`group` run becomes one grouped-digits block,
 *  `fraction` becomes the exact fraction digits, and every other part
 *  (`minusSign`, `currency`, `decimal`, `literal`, …) is copied verbatim —
 *  that is what carries the locale's own separators and placement through. */
function spliceExactDigits(
  parts: Intl.NumberFormatPart[],
  majorDigits: string,
  fractionDigits: string,
) {
  let groupSeparator = ',';
  for (const part of parts) {
    if (part.type === 'group') {
      groupSeparator = part.value;
      break;
    }
  }
  const groupedMajor = groupIntegerDigits(majorDigits, groupSeparator);
  let result = '';
  let integerEmitted = false;
  for (const part of parts) {
    if (part.type === 'integer' || part.type === 'group') {
      if (!integerEmitted) {
        result += groupedMajor;
        integerEmitted = true;
      }
    } else if (part.type === 'fraction') {
      result += fractionDigits;
    } else {
      result += part.value;
    }
  }
  return result;
}

function splitMinor(amountMinor: bigint | number | string) {
  const minor =
    typeof amountMinor === 'bigint' ? amountMinor : BigInt(amountMinor || 0);
  const negative = minor < BigInt(0);
  const abs = negative ? -minor : minor;
  return {
    fractionDigits: (abs % MINOR_UNITS_PER_MAJOR).toString().padStart(2, '0'),
    majorDigits: (abs / MINOR_UNITS_PER_MAJOR).toString(),
    negative,
  };
}

/**
 * Formats minor units (string | bigint | number) into a localized currency
 * amount, e.g. formatCurrency('129900', 'RSD') → "1.299,00 RSD" (sr-Latn).
 * Exact for any magnitude — including above Number.MAX_SAFE_INTEGER — since
 * the digits are read off the bigint, never composed into a `Number`.
 */
export function formatCurrency(
  amountMinor: bigint | number | string,
  currency: string,
  locale = 'sr-Latn',
) {
  const { fractionDigits, majorDigits, negative } = splitMinor(amountMinor);
  try {
    const parts = new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency',
    }).formatToParts(negative ? NEGATIVE_PROBE : POSITIVE_PROBE);
    return spliceExactDigits(parts, majorDigits, fractionDigits);
  } catch {
    // unknown ISO code (defensive) — raw display
    return `${negative ? '-' : ''}${majorDigits}.${fractionDigits} ${currency}`;
  }
}

/**
 * Formats an amount in MAJOR units (plain number — membership domain; NOT
 * minor units, not divided by 100). For the financial layer (BIGINT minor
 * string) use `formatCurrency`. No currency → number only (defensive).
 */
export function formatCurrencyMajor(
  amount: null | number | undefined,
  currency: null | string | undefined,
  locale = 'sr-Latn',
) {
  const value = Number(amount ?? 0);
  if (!currency) {
    return value.toFixed(2);
  }
  try {
    return new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency',
    }).format(value);
  } catch {
    // unknown ISO code (defensive) — raw display
    return `${value.toFixed(2)} ${currency}`;
  }
}

/**
 * Display without currency symbol (for tables/charts). Exact for any
 * magnitude, same bigint-digit-splice as `formatCurrency` — no `Number`
 * composed from the minor-unit value.
 */
export function formatAmount(
  amountMinor: bigint | number | string,
  locale = 'sr-Latn',
) {
  const { fractionDigits, majorDigits, negative } = splitMinor(amountMinor);
  const parts = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).formatToParts(negative ? NEGATIVE_PROBE : POSITIVE_PROBE);
  return spliceExactDigits(parts, majorDigits, fractionDigits);
}

/** Digits in groups of three behind a non-zero head ("1,000", "12 500"),
 *  optionally followed by a fraction behind `decimalPattern` (a regex
 *  fragment). */
const readGrouped = (text: string, group: string, decimalPattern: string) => {
  const match = text.match(
    new RegExp(
      `^([1-9]\\d{0,2}(?:${escapeRegExp(group)}\\d{3})+)(?:${decimalPattern}(\\d+))?$`,
    ),
  );
  if (!match) return null;
  const integer = match[1].split(group).join('');
  return Number(match[2] ? `${integer}.${match[2]}` : integer);
};

/**
 * Typed money → major units, or `null` when the text does not spell an amount.
 *
 * Three spellings are read, nothing else (fin-stat-7 F3, fin-stat-8):
 * - digits grouped by the locale's GROUP separator, optionally followed by
 *   the locale's decimal part ("1,000", "1,000.50" in en-US) — a money amount
 *   never carries three fraction digits, so this is unambiguous;
 * - digits grouped by a space, with either decimal separator ("12 500,50");
 * - a plain number with at most one separator of EITHER kind ("500,50",
 *   "12.345"), so the key a device keyboard offers is the one that works;
 *   the caller decides whether the fraction is exact to the cent.
 * A grouped integer never starts with 0 ("0,500" is fifty cents). Text outside
 * these shapes — letters, a sign, a second separator in a non-grouping place —
 * is refused rather than silently read as some other number.
 */
export function parseMoneyInput(input: string, locale: string) {
  const text = input.trim();
  const { group, decimal } = getNumberSeparators(locale);
  const grouped =
    readGrouped(text, group, escapeRegExp(decimal)) ??
    readGrouped(text, ' ', '[.,]');
  if (grouped !== null) return grouped;
  const plain = text.match(/^(\d+)(?:[.,](\d+))?$/);
  if (!plain) return null;
  return Number(plain[2] ? `${plain[1]}.${plain[2]}` : plain[1]);
}
